import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../../../services/api';
import { useSystemCheck } from '../../readiness';
import { useTranslation } from '../../../i18n';

interface BriefingData {
  simulation: {
    id: string;
    title: string;
    description: string;
    category: string;
    difficulty: string;
    timeLimitMinutes: number;
    passScore: number;
    learnerRole: string;
    allowedLanguages: string[];
    guidelines: string[];
    persona?: {
      name: string;
      role: string;
      temperament?: string;
      description?: string;
      avatarUrl?: string;
    };
    personas?: Array<{
      id?: string;
      name: string;
      role: string;
      temperament?: string;
      startingMood?: string;
      description?: string;
      objective?: string;
      personality?: string;
      communicationStyle?: string;
      skills?: string[];
      avatarUrl?: string;
      avatarType?: string;
    }>;
    rubrics?: Record<string, { name: string; weight: number; description: string }>;
    requiresCamera?: boolean;
    requiresMicrophone?: boolean;
    requiresSpeaker?: boolean;
    hardwareRequirements?: {
      requiresCamera?: boolean;
      requiresMicrophone?: boolean;
      requiresSpeaker?: boolean;
    };
  };
  personas?: Array<any>;
  policy?: {
    consentDocumentVersion?: string;
    audioRetentionDays?: number;
    consentDocumentUrl?: string;
  };
  consentRequirement?: {
    requiredVersion?: string;
    isConsentAccepted?: boolean;
    consentDocumentUrl?: string;
  };
  maxAttempts?: number;
  attemptsUsed?: number;
  attemptsRemaining?: number;
  attemptAllowance?: number;
  totalSimAttempts?: number;
  isAllowanceExceeded?: boolean;
  activeAttempt?: { id: string; attemptNumber?: number; status?: string } | null;
  prerequisiteGate?: {
    canStart?: boolean;
    activeAttemptId?: string | null;
    hasActiveAttempt?: boolean;
    activeAttempt?: { id: string; attemptNumber?: number; status?: string } | null;
    attemptsExhausted?: boolean;
    isAllowanceExceeded?: boolean;
    maxAttempts?: number;
    attemptsUsed?: number;
    attemptsRemaining?: number;
    attemptAllowance?: number;
    totalSimAttempts?: number;
    requiresConsent?: boolean;
    requiresCamera?: boolean;
    requiresMicrophone?: boolean;
    requiresSpeaker?: boolean;
  };
}

export function ScenarioBriefingPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get('lang') || undefined;
  const initialLanguage = urlLang || (typeof window !== 'undefined' ? sessionStorage.getItem('trainova_preferred_language') : null) || 'en';

  const translateCategory = (cat: string): string => {
    const catMap: Record<string, string> = {
      'customer service': 'category.customer_service',
      'customer_service': 'category.customer_service',
      'sales': 'category.sales',
      'compliance': 'category.compliance',
      'de_escalation': 'category.de_escalation',
      'de-escalation': 'category.de_escalation',
      'coaching': 'category.coaching',
      'general scenario': 'category.general_scenario',
      'general_scenario': 'category.general_scenario',
    };
    const key = catMap[cat.toLowerCase().trim()];
    return key ? t(key, undefined, cat) : cat;
  };

  const translateDifficulty = (diff: string): string => {
    const diffMap: Record<string, string> = {
      beginner: 'difficulty.beginner',
      intermediate: 'difficulty.intermediate',
      advanced: 'difficulty.advanced',
      standard: 'difficulty.standard',
    };
    const key = diffMap[diff.toLowerCase().trim()];
    return key ? t(key, undefined, diff) : diff;
  };

  const [data, setData] = useState<BriefingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const [hasConsented, setHasConsented] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage);
  const [isStarting, setIsStarting] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const lastFetchedIdRef = useRef<string | null>(null);
  const startBarRef = useRef<HTMLDivElement | null>(null);
  const consentRef = useRef<HTMLInputElement | null>(null);
  // Attempt creation can take several seconds on a cold provider; leaving the page while
  // the request is in flight must not yank the learner into the room afterwards.
  const launchCancelledRef = useRef(false);
  const [dockHidden, setDockHidden] = useState(false);

  // The dossier stacks four reading panels in front of the language / readiness / consent
  // steps, so on phones the start action lands ~4 screens below the fold. A pinned action
  // dock keeps it reachable and is withdrawn while the real start bar is on screen.
  // Rect math (not IntersectionObserver) so it also works while the tab is throttled.
  useEffect(() => {
    if (isLoading || loadError) return;
    const update = () => {
      const el = startBarRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) return;
      const vh = window.innerHeight || 0;
      const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      setDockHidden(visible / rect.height >= 0.4);
    };
    update();
    // capture:true — also fires when an inner overflow container is the scroller
    window.addEventListener('scroll', update, { passive: true, capture: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, { capture: true });
      window.removeEventListener('resize', update);
    };
  }, [isLoading, loadError]);

  const focusStartBar = () => {
    startBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => consentRef.current?.focus({ preventScroll: true }), 450);
  };

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('trainova_preferred_language', lang);
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (id && lastFetchedIdRef.current !== id) {
      lastFetchedIdRef.current = id;
      fetchBriefing(cancelled);
    }
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function fetchBriefing(cancelled = false) {
    try {
      setIsLoading(true);
      setLoadError(null);
      const res = await api.get<any>(`/api/v1/simulations/${id}/briefing`);
      if (cancelled) return;
      const payload = (res as any)?.data ?? res;
      const briefingPayload: BriefingData = payload?.data ?? payload;
      if (briefingPayload) {
        setData(briefingPayload);
      } else {
        setLoadError('No briefing data returned from server.');
      }
    } catch (err: any) {
      if (!cancelled) {
        setLoadError(err.message || 'Failed to load scenario briefing.');
      }
    } finally {
      if (!cancelled) {
        setIsLoading(false);
      }
    }
  }

  const s = data?.simulation || (data as any) || {};

  const maxAttempts = Number(s.maxAttempts ?? (data as any)?.maxAttempts ?? 3);
  const attemptsUsed = Number((data as any)?.attemptsUsed ?? s.attemptsUsed ?? 0);
  const attemptsRemaining = Math.max(0, Number((data as any)?.attemptsRemaining ?? s.attemptsRemaining ?? (maxAttempts - attemptsUsed)));
  const gate = (data as any)?.prerequisiteGate || {};
  const activeAttempt = gate.activeAttempt || (data as any)?.activeAttempt || s.activeAttempt;
  const activeAttemptId = gate.activeAttemptId || activeAttempt?.id;
  const hasActiveAttempt = Boolean(activeAttemptId);
  // Check attempt allowance and exhaustion rules
  const rawAllowanceExceeded = Boolean(gate.isAllowanceExceeded || (data as any)?.isAllowanceExceeded || s.isAllowanceExceeded);
  const attemptAllowance = Number((data as any)?.attemptAllowance ?? gate.attemptAllowance ?? s.attemptAllowance ?? 0);
  const totalSimAttempts = Number((data as any)?.totalSimAttempts ?? gate.totalSimAttempts ?? attemptsUsed);
  // Allowance is only truly blocked if user has consumed all their attempts or reached the scenario allowance
  const isAllowanceBlocked = rawAllowanceExceeded && (attemptsRemaining <= 0 || attemptsUsed >= maxAttempts || (attemptAllowance > 0 && attemptsUsed >= attemptAllowance));
  // A resumable attempt already owns its slot, so it outranks the exhausted banner:
  // otherwise a learner on their last allowed attempt sees "all attempts completed"
  // and loses the Resume action even though the server would resume it.
  const isAttemptsExhausted = !hasActiveAttempt && (
    attemptsRemaining <= 0 || attemptsUsed >= maxAttempts || isAllowanceBlocked || Boolean(gate.attemptsExhausted && attemptsRemaining <= 0)
  );

  const requiresMicrophone = s.requiresMicrophone !== undefined
    ? Boolean(s.requiresMicrophone)
    : (s.hardwareRequirements?.requiresMicrophone !== undefined
        ? Boolean(s.hardwareRequirements.requiresMicrophone)
        : (gate.requiresMicrophone !== undefined ? Boolean(gate.requiresMicrophone) : false));

  const requiresCamera = s.requiresCamera !== undefined
    ? Boolean(s.requiresCamera)
    : (s.hardwareRequirements?.requiresCamera !== undefined
        ? Boolean(s.hardwareRequirements.requiresCamera)
        : (gate.requiresCamera !== undefined ? Boolean(gate.requiresCamera) : false));

  const requiresSpeaker = s.requiresSpeaker !== undefined
    ? Boolean(s.requiresSpeaker)
    : (s.hardwareRequirements?.requiresSpeaker !== undefined
        ? Boolean(s.hardwareRequirements.requiresSpeaker)
        : (gate.requiresSpeaker !== undefined ? Boolean(gate.requiresSpeaker) : false));

  // Auto-run system checks inline on mount
  const {
    checks,
    readiness,
    isReady: isSystemReady,
    isCheckingAll,
    playTestChime,
    confirmSpeaker,
    speakerConfirmed,
    runAllChecks,
    retryCheck,
    stopAllStreams,
  } = useSystemCheck({
    requiresMicrophone,
    requiresCamera,
    requiresSpeaker,
    autoStart: true,
  });

  const micCheck = checks.find((c) => c.id === 'microphone');
  const camCheck = checks.find((c) => c.id === 'camera');
  const speakerCheck = checks.find((c) => c.id === 'speaker');
  const netCheck = checks.find((c) => c.id === 'network');

  // Gate on the real readiness result. Treating "microphone optional" as "ready"
  // let the CTA go live while the still-required network check was running or failed.
  const isAllReady = isSystemReady || readiness.isReady;

  async function handleStartSimulation() {
    // Mirror the CTA disabled state so a programmatic trigger cannot bypass
    // consent or a failing required hardware check.
    if (!hasActiveAttempt && (!hasConsented || !isAllReady)) return;
    try {
      setIsStarting(true);
      setStartError(null);
      launchCancelledRef.current = false;
      stopAllStreams();

      // Submit consent
      await api.post(`/api/v1/simulations/${id}/consent`, {
        documentVersion: data?.policy?.consentDocumentVersion || '1.0.0',
        consentStatus: 'ACCEPTED',
      }).catch(() => {});
      if (launchCancelledRef.current) return;

      // If active in-progress attempt already exists, resume it immediately without trying duplicate creation
      if (activeAttemptId) {
        navigate(`/simulations/${id}/run?attemptId=${activeAttemptId}&lang=${selectedLanguage}`);
        return;
      }

      // Fast server attempt initialization & quota verification on briefing stage (<100ms)
      // This prevents entering the heavy simulation room if attempts are exhausted or duplicate attempt is disallowed
      const res = await api.post<any>(`/api/v1/simulations/${id}/attempts`, {
        language: selectedLanguage,
      });
      if (launchCancelledRef.current) return;

      const payload = (res as any)?.data ?? res;
      const newAttemptId = payload?.attemptId || payload?.id || payload?.activeAttempt?.id || (res as any)?.activeAttempt?.id;

      if (newAttemptId) {
        // Cache the full server attempt payload so the simulation room hydrates instantly (<50ms)
        // without incurring a redundant 15-second resumeAttempt round-trip or trigger false timeouts.
        try {
          sessionStorage.setItem(`trainova_attempt_launch_${newAttemptId}`, JSON.stringify(payload));
        } catch {}
        navigate(`/simulations/${id}/run?attemptId=${newAttemptId}&lang=${selectedLanguage}`, {
          state: { initialAttemptData: payload }
        });
        return;
      }

      // If an active attempt already exists on the server, resume it
      const resumeId = (res as any)?.error?.attemptId || (res as any)?.activeAttempt?.id || payload?.error?.attemptId;
      if (resumeId) {
        if (payload && (payload.turns || payload.openingUtterance)) {
          try {
            sessionStorage.setItem(`trainova_attempt_launch_${resumeId}`, JSON.stringify(payload));
          } catch {}
        }
        navigate(`/simulations/${id}/run?attemptId=${resumeId}&lang=${selectedLanguage}`, {
          state: { initialAttemptData: payload }
        });
        return;
      }

      // Failed to create or resume attempt: display error and do not navigate without attemptId
      const errMsg = (res as any)?.error?.message || (res as any)?.message || 'Failed to initialize simulation session.';
      setStartError(errMsg);
      setIsStarting(false);
    } catch (err: any) {
      if (launchCancelledRef.current) return;
      const errPayload = err?.response?.data || err;
      const errMsg = errPayload?.error?.message || errPayload?.message || err?.message || 'Failed to initialize simulation session.';
      const errCode = errPayload?.error?.code || errPayload?.code;

      if (errCode === 'ACTIVE_ATTEMPT_EXISTS' && (errPayload?.error?.attemptId || errPayload?.activeAttempt?.id)) {
        const resumeId = errPayload?.error?.attemptId || errPayload?.activeAttempt?.id;
        navigate(`/simulations/${id}/run?attemptId=${resumeId}&lang=${selectedLanguage}`);
        return;
      }

      setStartError(errMsg);
      setIsStarting(false);
    }
  }

  const handleCancelLaunch = () => {
    launchCancelledRef.current = true;
    setIsStarting(false);
  };

  async function handlePlaySound() {
    setIsPlayingAudio(true);
    playTestChime();
    setTimeout(() => {
      setIsPlayingAudio(false);
      confirmSpeaker(true);
    }, 1200);
  }

  if (isLoading) {
    return (
      <div className="empty" style={{ background: 'var(--white)', border: '1px dashed #C3CEE0', borderRadius: '14px', padding: '60px 20px', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto 12px', width: '28px', height: '28px', border: '3px solid var(--line)', borderTopColor: 'var(--blue)', borderRadius: '50%' }} />
        <p style={{ color: 'var(--muted)' }}>{t('briefing.loading', undefined, 'Loading scenario briefing dossier…')}</p>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="empty" style={{ background: 'var(--bad-soft)', border: '1px solid #F1C7C7', borderRadius: '14px', padding: '40px 24px', textAlign: 'center', color: 'var(--bad)', maxWidth: '600px', margin: '40px auto' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>{t('briefing.error_title', undefined, 'Unable to load scenario')}</h3>
        <p style={{ margin: '8px 0 16px' }}>{loadError || t('scenarios.not_found', undefined, 'Scenario not found.')}</p>
        <Link to="/dashboard" className="btn ghost md" style={{ width: 'auto', display: 'inline-flex' }}>
          {t('briefing.back_to_dashboard', undefined, '← Back to Dashboard')}
        </Link>
      </div>
    );
  }

  const rubricSource = s.rubrics ?? (data as any)?.rubrics ?? {};
  const rubrics = Object.entries(rubricSource);
  const personasList: Array<any> = (Array.isArray(s.personas) && s.personas.length > 0)
    ? s.personas
    : (Array.isArray((data as any)?.personas) && (data as any).personas.length > 0
      ? (data as any).personas
      : (s.persona ? [s.persona] : []));
  const allowedLanguages = Array.isArray(s.allowedLanguages) && s.allowedLanguages.length > 0
    ? s.allowedLanguages
    : (Array.isArray((data as any)?.allowedLanguages) && (data as any).allowedLanguages.length > 0 ? (data as any).allowedLanguages : ['en', 'hi', 'ta']);

  const LANGUAGE_LABELS: Record<string, string> = {
    en: 'English (US / IN)',
    hi: 'Hindi (हिंदी)',
    ta: 'Tamil (தமிழ்)',
    te: 'Telugu (తెలుగు)',
    kn: 'Kannada (ಕನ್ನಡ)',
    mr: 'Marathi (मराठी)',
    bn: 'Bengali (বাংলা)',
    es: 'Spanish (Español)',
    fr: 'French (Français)',
    de: 'German (Deutsch)',
  };

  const getStatusBadge = (checkItem: any, isOptional: boolean = false) => {
    if (!checkItem) {
      return isOptional ? (
        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--blue-soft, #EBF3FF)', color: 'var(--blue, #0D4FBD)' }}>
          {t('briefing.status.optional', undefined, 'Optional')}
        </span>
      ) : (
        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: '#F1F5F9', color: '#64748B' }}>
          {t('briefing.status.checking', undefined, 'Checking…')}
        </span>
      );
    }

    if (checkItem.status === 'passed') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--ok-soft, #E8F8F0)', color: 'var(--ok, #107E4A)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {t('briefing.status.ready', undefined, '✓ Ready')}
        </span>
      );
    }

    if (checkItem.status === 'running') {
      return (
        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: '#FEF3C7', color: '#B45309', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          {t('briefing.status.testing', undefined, '⏳ Testing…')}
        </span>
      );
    }

    if (isOptional) {
      if (checkItem.status === 'passed') {
        return (
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--ok-soft, #E8F8F0)', color: 'var(--ok, #107E4A)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {t('briefing.status.optional_passed', undefined, '✓ Optional')}
          </span>
        );
      }
      return (
        <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--blue-soft, #EBF3FF)', color: 'var(--blue, #0D4FBD)' }}>
          {t('briefing.status.optional', undefined, 'Optional')}
        </span>
      );
    }

    if (checkItem.status === 'failed') {
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--bad-soft, #FDE8E8)', color: 'var(--bad, #C81E1E)' }}>
            {t('briefing.status.failed', undefined, '⚠️ Failed')}
          </span>
          <button
            type="button"
            onClick={() => retryCheck(checkItem.id)}
            style={{ fontSize: '11px', padding: '2px 6px', border: '1px solid var(--line, #CBD5E1)', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: 'var(--ink, #1E293B)' }}
          >
            {t('common.retry', undefined, '↻ Retry')}
          </button>
        </div>
      );
    }

    return (
      <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: '#F1F5F9', color: '#64748B' }}>
        {t('briefing.status.pending', undefined, 'Pending')}
      </span>
    );
  };

  return (
    <div className="stage brief" style={{ background: 'var(--surface)', display: 'grid', gap: '20px' }}>
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className="link b-back"
        style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', border: 0, background: 'none', padding: 0 }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px' }}>
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        {t('scenarios.back_to_simulations', undefined, 'Back to simulations')}
      </button>

      {/* Briefing Header */}
      <div className="b-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px 24px', flexWrap: 'wrap' }}>
        <div>
          <div className="eyebrow" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '6px' }}>
            <span className="stat s-new">{t('status.ready_to_practice', undefined, 'Ready to practice')}</span>
            <span className="role" style={{ margin: 0 }}>{translateCategory(s.category || 'Customer Service')}</span>
          </div>
          <h1 style={{ fontFamily: 'var(--display)', fontSize: '26px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
            {s.title || t('briefing.title', undefined, 'Simulation Briefing')}
          </h1>
        </div>

        <dl className="b-strip">
          <div>
            <dt>{t('dashboard.facts.time_limit', undefined, 'Time limit')}</dt>
            <dd>{s.timeLimitMinutes || 15} <small>{t('dashboard.facts.mins', undefined, 'mins')}</small></dd>
          </div>
          <div>
            <dt>{t('dashboard.facts.attempts', undefined, 'Attempts')}</dt>
            <dd style={{ color: isAttemptsExhausted ? 'var(--bad, #C23B3B)' : undefined }}>
              {attemptsRemaining} <small>/ {maxAttempts}</small>
            </dd>
          </div>
          <div>
            <dt>{t('dashboard.facts.benchmark', undefined, 'Benchmark')}</dt>
            <dd>{s.passScore || 70}%</dd>
          </div>
          <div>
            <dt>{t('scenarios.difficulty_label', undefined, 'Difficulty')}</dt>
            <dd>{translateDifficulty(s.difficulty || 'Intermediate')}</dd>
          </div>
          <div>
            <dt>{t('scenarios.your_role', undefined, 'Your Role')}</dt>
            <dd>{s.learnerRole || t('briefing.default_role', undefined, 'Customer Support Rep')}</dd>
          </div>
        </dl>
      </div>

      {/* Main Briefing Columns */}
      <div className="b-cols">
        {/* Left Stack */}
        <div className="b-stack">
          {/* Objective & Description */}
          <div className="b-panel">
            <div className="b-ph">
              <h2>
                <span className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                </span>
                {t('briefing.background_mission_title', undefined, 'Scenario Background & Learner Mission')}
              </h2>
            </div>
            <div className="b-pb">
              <p style={{ fontSize: '14.5px', color: '#2A3650', lineHeight: '1.6' }}>
                {s.description}
              </p>
              <div className="b-obj">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <div>
                  <div className="b-lbl">{t('briefing.key_mission_label', undefined, 'Key Mission')}</div>
                  <div style={{ fontSize: '13.5px', color: 'var(--ink)', fontWeight: 500 }}>
                    {t('briefing.default_mission', undefined, 'De-escalate the customer, clarify the underlying request, and resolve the inquiry within compliance standards.')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Customer Personas Section */}
          <div className="b-panel">
            <div className="b-ph">
              <h2>
                <span className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </span>
                {personasList.length > 1
                  ? t('briefing.personas_dossier_title_plural', { count: personasList.length }, `Scenario Personas Dossier (${personasList.length} AI Participants)`)
                  : t('briefing.persona_dossier_title', undefined, 'Customer Profile Dossier')}
              </h2>
            </div>
            <div className="b-pb">
              <div style={{ display: 'grid', gap: '16px' }}>
                {personasList.map((p: any, idx: number) => {
                  const pName = p.name || t('briefing.persona_number', { n: idx + 1 }, `Persona #${idx + 1}`);
                  const pRole = p.role || t('briefing.persona_role_participant', undefined, 'Participant');
                  const pMood = p.temperament || p.startingMood || t('briefing.persona_temperament_challenging', undefined, 'Challenging / Urgent');
                  const pDesc = p.description || p.personaObjective || p.objective || p.personality || '';
                  const pAvatar = p.avatarUrl || (idx === 0 ? s.persona?.avatarUrl : null);
                  const pSkills = Array.isArray(p.skills) ? p.skills : [];

                  return (
                    <div
                      key={p.id || idx}
                      style={{
                        padding: personasList.length > 1 ? '16px' : '0',
                        background: personasList.length > 1 ? '#F8FAFC' : 'transparent',
                        borderRadius: personasList.length > 1 ? '10px' : '0',
                        border: personasList.length > 1 ? '1px solid #E2E8F0' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                        {pAvatar ? (
                          <img
                            src={pAvatar}
                            alt={pName}
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '2px solid #CBD5E1',
                              flexShrink: 0,
                              background: '#E2E8F0'
                            }}
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            className="avatar"
                            style={{
                              width: '48px',
                              height: '48px',
                              fontSize: '15px',
                              fontWeight: 700,
                              background: idx === 0 ? 'var(--blue)' : idx === 1 ? '#7C3AED' : '#059669',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '50%',
                              flexShrink: 0
                            }}
                          >
                            {pName.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontFamily: 'var(--display)', fontSize: '16px', fontWeight: 600, color: 'var(--ink)' }}>
                              {pName}
                            </span>
                            {personasList.length > 1 && (
                              <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px', background: '#E0E7FF', color: '#3730A3' }}>
                                {t('briefing.persona_number', { n: idx + 1 }, `Persona #${idx + 1}`)}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>
                            {t('briefing.persona_role_label', undefined, 'Role:')} <strong style={{ color: 'var(--ink)' }}>{pRole}</strong> • {t('briefing.persona_temperament_label', undefined, 'Temperament:')} <span style={{ color: '#D97706', fontWeight: 500 }}>{pMood}</span>
                          </div>
                        </div>
                      </div>

                      {pDesc && (
                        <p style={{ fontSize: '13.5px', color: '#2A3650', marginTop: personasList.length > 1 ? '10px' : '8px', lineHeight: '1.5', margin: '8px 0 0 0' }}>
                          {pDesc}
                        </p>
                      )}

                      {pSkills.length > 0 && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                          {pSkills.map((sk: string, sIdx: number) => (
                            <span
                              key={sIdx}
                              style={{
                                fontSize: '11.5px',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                background: '#EFF6FF',
                                color: '#1D4ED8',
                                border: '1px solid #BFDBFE'
                              }}
                            >
                              {sk}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Evaluation Competencies & Rubrics */}
          <div className="b-panel">
            <div className="b-ph">
              <h2>
                <span className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                </span>
                {t('briefing.competency_matrix_title', undefined, 'Evaluation Competency Matrix')}
              </h2>
            </div>
            <div className="b-pb">
              <div className="b-comps">
                {rubrics.length > 0 ? (
                  rubrics.map(([key, rawR]) => {
                    const r = rawR as { name?: string; weight?: number; description?: string };
                    return (
                      <div key={key} className="b-comp">
                        <b>{r?.name || key}</b>
                        <div className="w">
                          <span>{t('briefing.rubric_weight', undefined, 'Weight')}</span>
                          <strong>{r?.weight || 25}%</strong>
                        </div>
                        <div className="wbar">
                          <i style={{ width: `${r?.weight || 25}%` }}></i>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div className="b-comp">
                      <b>{t('briefing.rubric_empathy', undefined, 'Empathy & Active Listening')}</b>
                      <div className="w"><span>{t('briefing.rubric_weight', undefined, 'Weight')}</span><strong>30%</strong></div>
                      <div className="wbar"><i style={{ width: '30%' }}></i></div>
                    </div>
                    <div className="b-comp">
                      <b>{t('briefing.rubric_resolution', undefined, 'Problem Resolution')}</b>
                      <div className="w"><span>{t('briefing.rubric_weight', undefined, 'Weight')}</span><strong>40%</strong></div>
                      <div className="wbar"><i style={{ width: '40%' }}></i></div>
                    </div>
                    <div className="b-comp">
                      <b>{t('briefing.rubric_compliance', undefined, 'Compliance & Protocol')}</b>
                      <div className="w"><span>{t('briefing.rubric_weight', undefined, 'Weight')}</span><strong>30%</strong></div>
                      <div className="wbar"><i style={{ width: '30%' }}></i></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Guidelines */}
          <div className="b-panel">
            <div className="b-ph">
              <h2>
                <span className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </span>
                {t('briefing.rules_title', undefined, 'Rules of Engagement')}
              </h2>
            </div>
            <div className="b-pb">
              <ul className="b-rules">
                <li>
                  <div className="ri">🎙️</div>
                  <div>
                    <b>{t('briefing.rule1_title', undefined, 'Natural Speech Required')}</b>
                    {t('briefing.rule1_desc', undefined, 'Speak clearly as you would on a live voice customer call.')}
                  </div>
                </li>
                <li>
                  <div className="ri">⚡</div>
                  <div>
                    <b>{t('briefing.rule2_title', undefined, 'Real-Time Interactive AI')}</b>
                    {t('briefing.rule2_desc', undefined, 'The AI persona will respond dynamically based on your questions and empathy.')}
                  </div>
                </li>
                <li>
                  <div className="ri">🛡️</div>
                  <div>
                    <b>{t('briefing.rule3_title', undefined, 'Privacy & Video Telemetry')}</b>
                    {t('briefing.rule3_desc', undefined, 'Audio/video telemetry is processed securely for AI coaching and scoring.')}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Right Stack: Language & Start Bar */}
        <div className="b-stack b-stack--side">
          {/* Language Selection */}
          <div className="b-panel b-langp done">
            <div className="b-ph">
              <h2>
                <span className="ic">🌐</span>
                {t('briefing.language_title', undefined, 'Language Required')}
              </h2>
              <span className="b-step">{t('briefing.step_1', undefined, 'Step 1')}</span>
            </div>
            <div className="b-pb">
              <p className="b-ltxt">{t('briefing.select_language_desc', undefined, 'Select your preferred conversation language for this simulation:')}</p>
              <div className="b-dd">
                <div className="gl">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <select
                  value={selectedLanguage}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  aria-label={t('briefing.select_language_aria', undefined, 'Preferred conversation language')}
                >
                  {allowedLanguages.map((lang: string) => (
                    <option key={lang} value={lang}>
                      {LANGUAGE_LABELS[lang] || lang.toUpperCase()}
                    </option>
                  ))}
                </select>
                <span className="b-avail">{t('briefing.ready_badge', undefined, 'Ready')}</span>
              </div>
            </div>
          </div>

          {/* System Pre-Flight / Readiness Inline Card */}
          <div className="b-panel">
            <div className="b-ph">
              <h2>
                <span className="ic">⚙️</span>
                {t('briefing.readiness_title', undefined, 'System Readiness')}
              </h2>
              <span className="b-step" style={{ background: isAllReady ? 'var(--ok, #107E4A)' : 'var(--blue, #0D4FBD)', color: '#fff', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                {isAllReady ? t('briefing.status.ready', undefined, '✓ Ready') : t('briefing.step_2', undefined, 'Step 2')}
              </span>
            </div>
            <div className="b-pb" style={{ display: 'grid', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--muted)' }}>
                <span>{t('briefing.auto_diagnostics', undefined, 'Auto-diagnostics running')}</span>
                <button
                  type="button"
                  className="b-rerun"
                  onClick={() => runAllChecks()}
                  disabled={isCheckingAll}
                  style={{ border: 'none', background: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0 }}
                >
                  {isCheckingAll ? t('briefing.testing_button', undefined, 'Testing…') : t('briefing.rerun_all', undefined, '↻ Re-run All')}
                </button>
              </div>

              {/* Microphone Check */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: 'var(--surface, #F8FAFC)', border: '1px solid var(--line, #E2E8F0)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px' }}>🎙️</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                      {t('briefing.mic_status', undefined, 'Microphone')} {!requiresMicrophone ? t('briefing.optional_paren', undefined, '(Optional)') : t('briefing.compulsory_paren', undefined, '(Compulsory)')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                      {t('briefing.mic_subtext', undefined, 'Voice input capture')}
                    </div>
                  </div>
                </div>
                {getStatusBadge(micCheck, !requiresMicrophone)}
              </div>

              {/* Camera Check */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: 'var(--surface, #F8FAFC)', border: '1px solid var(--line, #E2E8F0)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px' }}>📹</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                      {t('briefing.camera_title', undefined, 'Camera & Video')} {!requiresCamera ? t('briefing.optional_paren', undefined, '(Optional)') : t('briefing.compulsory_paren', undefined, '(Compulsory)')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                      {t('briefing.camera_subtext', undefined, 'Visual attention telemetry')}
                    </div>
                  </div>
                </div>
                {getStatusBadge(camCheck, !requiresCamera)}
              </div>

              {/* Speaker Check & Inline Test */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: 'var(--surface, #F8FAFC)', border: '1px solid var(--line, #E2E8F0)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px' }}>🔊</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                      {t('briefing.speaker_title', undefined, 'Speaker Playback')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                      {t('briefing.speaker_subtext', undefined, 'AI customer persona voice')}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handlePlaySound}
                    disabled={isPlayingAudio}
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 600,
                      padding: '4px 8px',
                      borderRadius: '6px',
                      border: '1px solid var(--line, #CBD5E1)',
                      background: isPlayingAudio ? '#EBF3FF' : '#fff',
                      color: isPlayingAudio ? 'var(--blue)' : 'var(--ink)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {isPlayingAudio ? t('briefing.sound_playing', undefined, '🔊 Playing…') : t('briefing.test_sound', undefined, '▶ Test Sound')}
                  </button>
                  {speakerConfirmed || speakerCheck?.status === 'passed' ? (
                    <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--ok-soft, #E8F8F0)', color: 'var(--ok, #107E4A)' }}>
                      {t('briefing.status.ok', undefined, '✓ OK')}
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Network Latency Check */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: '10px', background: 'var(--surface, #F8FAFC)', border: '1px solid var(--line, #E2E8F0)' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '18px' }}>⚡</span>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                      {t('briefing.network_title', undefined, 'Network Latency')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                      {t('briefing.network_subtext', undefined, 'AI server connection')}
                    </div>
                  </div>
                </div>
                {getStatusBadge(netCheck, false)}
              </div>
            </div>
          </div>

          {/* Start CTA Bar */}
          <div className="b-startbar" ref={startBarRef} style={{ display: 'grid', gap: '14px', background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '14px', padding: '18px' }}>
            {isAttemptsExhausted ? (
              <>
                <div style={{ background: 'var(--bad-soft, #FDECEC)', border: '1px solid #F1C7C7', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--bad, #C23B3B)', fontWeight: 600, fontSize: '14.5px' }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <span>{isAllowanceBlocked ? t('briefing.allowance_reached_title', undefined, 'Scenario Attempt Allowance Reached') : t('briefing.all_attempts_completed_title', undefined, 'All Attempts Completed')}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#5B6781', lineHeight: '1.45' }}>
                    {isAllowanceBlocked
                      ? (attemptAllowance > 0
                          ? t('briefing.allowance_reached_desc_with_total', { count: totalSimAttempts, total: attemptAllowance }, `This scenario's attempt allowance is fully used (${totalSimAttempts} of ${attemptAllowance}). Purchase an attempt pack or ask your administrator to add attempts.`)
                          : t('briefing.allowance_reached_desc', undefined, 'This scenario\'s attempt allowance is fully used. Purchase an attempt pack or ask your administrator to add attempts.'))
                      : t('briefing.max_attempts_reached_desc', { used: attemptsUsed, max: maxAttempts }, `You have reached the maximum allowed attempts (${attemptsUsed} of ${maxAttempts} completed) for this simulation. No additional attempts can be started.`)}
                  </p>
                </div>

                <button
                  type="button"
                  className="btn off"
                  disabled
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    background: '#E2E8F0',
                    color: '#94A3B8',
                    border: 'none',
                    cursor: 'not-allowed',
                  }}
                >
                  {isAllowanceBlocked
                    ? t('briefing.allowance_reached_btn', undefined, 'Attempt Allowance Reached')
                    : t('briefing.max_attempts_reached_btn', { used: attemptsUsed, max: maxAttempts }, `Max Attempts Reached (${attemptsUsed}/${maxAttempts})`)}
                </button>

                <Link
                  to="/attempts"
                  className="btn ghost"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '10px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--blue, #0D4FBD)',
                    border: '1px solid var(--line)',
                    textDecoration: 'none',
                  }}
                >
                  {t('briefing.view_past_scorecards', undefined, 'View Past Attempt Scorecards →')}
                </Link>
              </>
            ) : hasActiveAttempt ? (
              <>
                <div style={{ background: 'var(--warn-soft, #FFF4E0)', border: '1px solid #FFE0B2', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--warn, #B26A00)', fontWeight: 600, fontSize: '14.5px' }}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    <span>{t('briefing.active_attempt_title', undefined, 'Active Attempt In Progress')}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', color: '#5B6781', lineHeight: '1.45' }}>
                    {t('briefing.active_attempt_desc', { attempt: activeAttempt?.attemptNumber ? t('briefing.active_attempt_number_tag', { n: activeAttempt.attemptNumber }, ` (Attempt #${activeAttempt.attemptNumber})`) : '' }, `You already have an active attempt${activeAttempt?.attemptNumber ? ` (Attempt #${activeAttempt.attemptNumber})` : ''} in progress. Duplicate attempts are not allowed. You can resume your session directly.`)}
                  </p>
                </div>

                {startError && (
                  <div style={{ background: 'var(--bad-soft, #FDECEC)', border: '1px solid #F1C7C7', borderRadius: '8px', padding: '10px 12px', color: 'var(--bad, #C23B3B)', fontSize: '13px' }}>
                    {startError}
                  </div>
                )}

                <button
                  type="button"
                  className="btn primary b-go"
                  disabled={isStarting}
                  onClick={handleStartSimulation}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    background: isStarting ? '#CBD5E1' : 'var(--warn, #B26A00)',
                    color: '#fff',
                    border: 'none',
                    cursor: isStarting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isStarting ? t('briefing.resuming', undefined, 'Resuming Simulation…') : t('briefing.resume_in_progress', undefined, 'Resume In-Progress Attempt →')}
                </button>
              </>
            ) : (
              <>
                <label className="b-consent" style={{ display: 'flex', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#2A3650', lineHeight: '1.4' }}>
                  <input
                    type="checkbox"
                    ref={consentRef}
                    checked={hasConsented}
                    onChange={(e) => setHasConsented(e.target.checked)}
                    style={{ marginTop: '2px' }}
                  />
                  <span>
                    {t('briefing.consent_text', undefined, 'I agree to the assessment recording policy and consent to AI-based conversation analysis and competency evaluation.')}
                    {((data as any)?.consentRequirement?.consentDocumentUrl || data?.policy?.consentDocumentUrl) && (
                      <>
                        {' '}
                        <a
                          href={(data as any)?.consentRequirement?.consentDocumentUrl || data?.policy?.consentDocumentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--blue, #0D4FBD)', textDecoration: 'underline', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('briefing.view_terms', undefined, 'View Terms & Consent Policy ↗')}
                        </a>
                      </>
                    )}
                  </span>
                </label>

                {startError && (
                  <div style={{ background: 'var(--bad-soft, #FDECEC)', border: '1px solid #F1C7C7', borderRadius: '8px', padding: '10px 12px', color: 'var(--bad, #C23B3B)', fontSize: '13px' }}>
                    {startError}
                  </div>
                )}

                {hasConsented && !isAllReady && (
                  <div style={{ background: 'var(--warn-soft, #FFF4E0)', border: '1px solid #FFE0B2', borderRadius: '8px', padding: '10px 12px', color: '#8A5A00', fontSize: '13px', lineHeight: '1.45' }}>
                    {isCheckingAll
                      ? t('briefing.running_hardware_checks', undefined, 'Running the required hardware checks. The room unlocks as soon as they pass.')
                      : t('briefing.complete_system_checks', undefined, 'Complete the required system checks above to unlock the room. Retry any failed check.')}
                  </div>
                )}

                <button
                  type="button"
                  className="btn primary b-go"
                  disabled={!hasConsented || !isAllReady || isStarting}
                  onClick={handleStartSimulation}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    background: !hasConsented || !isAllReady || isStarting ? '#CBD5E1' : 'var(--blue, #0D4FBD)',
                    color: '#fff',
                    border: 'none',
                    cursor: !hasConsented || !isAllReady || isStarting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {isStarting
                    ? t('briefing.launching', undefined, 'Launching Simulation…')
                    : !hasConsented
                      ? t('briefing.consent_required_btn', undefined, 'Consent Required to Enter Room')
                      : !isAllReady
                        ? (isCheckingAll ? t('briefing.checking_hardware_btn', undefined, 'Checking Hardware…') : t('briefing.complete_checks_btn', undefined, 'Complete System Checks'))
                        : t('briefing.enter_room', undefined, 'Enter Simulation Room →')}
                </button>

                <p className="b-note" style={{ margin: 0, fontSize: '12px', color: 'var(--muted)', textAlign: 'center' }}>
                  {t('briefing.start_note', { remaining: attemptsRemaining, max: maxAttempts }, `Simulation time starts immediately upon entering the room. (${attemptsRemaining} of ${maxAttempts} attempts remaining)`)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Phone action dock — pinned start action that sits above the app tab bar */}
      {!isAttemptsExhausted && (
        <div className={`b-cta-dock${dockHidden ? ' b-cta-dock--hidden' : ''}`} role="region" aria-label={t('briefing.start_simulation_aria', undefined, 'Start simulation')}>
          <div className="b-cta-dock__meta">
            <span className="b-cta-dock__step">
              {isStarting
                ? t('briefing.launching_short', undefined, 'Launching…')
                : hasActiveAttempt
                  ? t('briefing.attempt_in_progress_short', undefined, 'Attempt in progress')
                  : !hasConsented
                    ? t('briefing.dock_step_consent', undefined, 'Step 3 · consent required')
                    : !isAllReady
                      ? t('briefing.dock_step_hardware', undefined, 'Step 2 · hardware checks')
                      : t('briefing.dock_ready', undefined, 'Ready to launch')}
            </span>
            <span className="b-cta-dock__quota">
              {hasActiveAttempt
                ? t('briefing.dock_ready_to_resume', undefined, 'Session ready to resume')
                : t('briefing.dock_attempts_left', { remaining: attemptsRemaining, max: maxAttempts }, `${attemptsRemaining} of ${maxAttempts} attempts left`)}
            </span>
          </div>
          <button
            type="button"
            className="btn primary b-go b-cta-dock__btn"
            disabled={isStarting}
            onClick={() => {
              if (!hasActiveAttempt && (!hasConsented || !isAllReady)) {
                focusStartBar();
                return;
              }
              handleStartSimulation();
            }}
            style={{
              width: 'auto',
              maxWidth: '62%',
              padding: '0 14px',
              fontSize: '14.5px',
              borderRadius: '10px',
              background: isStarting ? '#CBD5E1' : (hasActiveAttempt ? 'var(--warn, #B26A00)' : 'var(--blue, #0D4FBD)'),
              color: '#fff',
              border: 'none',
              cursor: isStarting ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {isStarting
              ? t('briefing.launching_short', undefined, 'Launching…')
              : hasActiveAttempt
                ? t('briefing.dock_resume_btn', undefined, 'Resume Attempt →')
                : !hasConsented
                  ? t('briefing.dock_consent_btn', undefined, 'Review & consent ↓')
                  : !isAllReady
                    ? t('briefing.dock_checks_btn', undefined, 'Complete checks ↓')
                    : t('briefing.dock_enter_btn', undefined, 'Enter Room →')}
          </button>
        </div>
      )}

      {/* Launch progress overlay */}
      {isStarting && (
        <div className="b-launch-overlay" role="status" aria-live="polite">
          <div className="b-launch-card">
            <div className="spinner" aria-hidden="true" />
            <h3>{hasActiveAttempt ? t('briefing.overlay_resuming_title', undefined, 'Resuming your simulation…') : t('briefing.overlay_preparing_title', undefined, 'Preparing your simulation room…')}</h3>
            <p>
              {t('briefing.overlay_desc', undefined, 'Creating the attempt, locking your voice settings and loading the scenario. This can take a few seconds — keep this tab open.')}
            </p>
            <button type="button" className="btn ghost md" onClick={handleCancelLaunch}>
              {t('briefing.overlay_cancel', undefined, 'Cancel and stay here')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
